import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EducationService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-details/components/employee-education/services/education.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { Education, EducationVerbose } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-details/components/employee-education/models/education.model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { api_routes, db_tables } from '@app/consts';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';

@Component({
  selector: 'app-employee-education',
  templateUrl: './employee-education.component.html',
  styleUrls: ['./employee-education.component.scss']
})
export class EmployeeEducationComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: "qualificationName", title: "qualificationName", type: "navigation", tableId: "tfi_EducationQualificationName"},
    { field: "discipline", subField: "text", title: "discipline", tableId: "tfi_EducationDiscipline"},
    { field: "qualificationType", subField: "text", title: "qualification Type", tableId: "tfi_EductionQualification"},
    { field: "designation", subField: "text", title: "designation", tableId: "tfi_EducationDesignation"},
    { field: "dateCompleted", type: "date", title: "date Completed", tableId: "tfi_EducationDateCompleted"},
    { field: "institution", title: "institution", tableId: "tfi_EducationInstitution"},
    { field: "location", title: "location", tableId: "tfi_EducationLocation"},
    { field: "comments", title: "comments", tableId: "tfi_EducationComments"},
  ];
  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_CMowQ5pCGFPq5k'
  changeReasonFormId: string = 'frm_a7t8ZRnOCnYZUR';
  formValid: boolean;
  private sortString: string = 'dateCompleted-desc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.EDUCATION}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private educationService: EducationService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
        params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.view = this.getEducations();
    this.getTargetEmployeeTablePermissions();
  }

  getEducations() {
    this.isLoading = true;

    this.educationService.getEducations(this.employeeID, this.pageSize, this.skip, this.sortString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  getEducation(education?: Education) {
    if(education === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.educationService.getEducation(this.employeeID, education.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  openFormDialog(education?: EducationVerbose) {
    let formData = {
      id: education ? education.id : null,
      asOf: moment().format(),
      changeReason: '',
      changeReasonComments: '',
      institution: education ? education.institution : null,
      dateCompleted: education ? education.dateCompleted : null,
      qualificationName: education ? education.qualificationName : null,
      designation: education ? education.designation?.id : null,
      qualificationType: education ? education.qualificationType?.id : null,
      discipline: education ? education.discipline?.id : null,
      location: education ? education.location : null,
      comments: education ? education.comments : null,
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('Education')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();
    if (formData.dateCompleted) {formData.dateCompleted = moment(formData.dateCompleted).format().slice(0, 10);}
    if(!formData.id){
      this.educationService.createEducation(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getEducations();
        }
      );
    }
    else {
      this.educationService.updateEducation(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getEducations();
        }
      );
    }
  }

  deleteEducations(educationIds: string[]) {
    const observables = educationIds.map(selectedItem => defer(() => this.educationService.deleteEducation(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getEducations();
        this.clearSelectedItems = !this.clearSelectedItems;
        this.snackbarService.openSnackBar('Deleted successfully', 'clear', 'success');
      },
      err => {
        this.snackbarService.openSnackBar(err, 'clear', 'warn');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getEducations();
  }
}


